import "./Table.scss";
import React, { useCallback, useMemo, memo, useState, useEffect } from "react";
import { useLangNavigate, LangLink } from "../../../utils/langNavigation";
import {
  Breadcrumbs,
  Text,
  Button,
  useTable,
  Table,
  TablePager,
  TransitionChevron,
  Layout,
  Heading,
  FormFieldSelect,
  useForm,
} from "@netapp/bxp-design-system-react";
import Select, { components } from "react-select";
import _ from "lodash";
import { CopyReplicateIcon } from "@netapp/bxp-style/react-icons/Action";
import { BulletIcon } from "@netapp/bxp-style/react-icons/General";
import { Tooltip } from "@netapp/bxp-design-system-react";
import DOMPurify from 'dompurify';
import { useTranslation } from "react-i18next";

const sanitizeValue = (value) => {
  if (typeof value === "string") {
    return value.replace(/[\r\n]+/g, " ");
  }
  return value;
};

const convertToCSV = (data) => {
  const csvRows = [];
  const headers = [
    "Advisory Id",
    "Title",
    "Product",
    "Product Status",
    "Advisory Status",
    "CVEs",
    "Description",
    "Comment",
    "Version",
    "Date",
    "Year",
    "Fix Links",
    "Workaround",
    "Burt",
    "Jira",
  ];
  csvRows.push(headers.join(","));

  data.forEach((row) => {
    const values = [
      `"${row.id}"`,
      `"${sanitizeValue(row.title)}"`,
      `"${sanitizeValue(row.product)}"`,
      `"${row.product_status}"`,
      `"${row.kb_status}"`,
      `"${row.score}"`,
      `"${sanitizeValue(row.description)}"`,
      `"${sanitizeValue(row.comment)}"`,
      `"${row.version}"`,
      `"${row.date}"`,
      `"${sanitizeValue(row.year)}"`,
      `"${sanitizeValue(row.fix_links ? row.fix_links : "")}"`,
      `"${sanitizeValue(row.kb_workarounds)}"`,
      `"${row.burt}"`,
      `"${row.jira}"`,
    ];
    csvRows.push(values.join(","));
  });

  return csvRows.join("\n");
};

const getSeverityColor = (severity) => {
  const severityParts = severity?.split(" ");
  severity = severityParts ? severityParts[0] : severity;
  switch (severity?.toLowerCase()) {
    case "critical":
      return "red";
    case "high":
      return "orange";
    case "medium":
      return "purple";
    case "low":
      return "green";
    default:
      return "gray";
  }
};

const downloadCSV = (csv, filename) => {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = DOMPurify.sanitize(url);
  a.setAttribute("download", filename);
  a.click();
};

// Custom Option to show tooltips for long product labels
const Option = (props) => {
  const label = props.label;
  const tooltip = props.data?.tooltip;
  return (
    <components.Option {...props}>
      {tooltip ? (
        <Tooltip content={tooltip} placement="right">
          <span>{label}</span>
        </Tooltip>
      ) : (
        <span>{label}</span>
      )}
    </components.Option>
  );
};

// Ensure the select menu overlays other content (especially in mobile)
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
  menu: (base) => ({ ...base, zIndex: 10000 }),
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    height: 40,
    boxShadow: state.isFocused ? base.boxShadow : "none",
  }),
  valueContainer: (base) => ({
    ...base,
    height: 40,
    padding: "0 12px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 40,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
};

const Table1 = ({ openSubscribeModalFromTable }) => {
  const { t } = useTranslation();
  const navigate = useLangNavigate();
  const [selectedCVE, setSelectedCVE] = useState("");
  const [keyword, setKeyword] = useState("");
  const [products, setProducts] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const [totalCount, setTotalCount] = useState(0);
  const [publishedStartDate, setPublishedStartDate] = useState("");
  const [publishedEndDate, setPublishedEndDate] = useState("");
  const [updatedStartDate, setUpdatedStartDate] = useState("");
  const [updatedEndDate, setUpdatedEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const pageCount = Math.ceil(totalCount / pageSize);
  const rowsCountArray = Array(totalCount);
  const [copied, setCopied] = useState(false);
  const form = useForm({ severity: "", product: "" }); // note: product key

  const baseUrl = window.location.origin.replace(/\/advisory$/, "");

  const handleCopy = (event, cve_id) => {
    event.stopPropagation();
    if (cve_id) {
      cve_id = cve_id.join(", ");
      navigator.clipboard
        .writeText(cve_id)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
        });
    }
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
  };

  const tableColumns = useMemo(() => {
    return [
      {
        header: t("secPortal_table_title.other"),
        accessor: "title",
        id: "kb_title",
        width: 350,
        Renderer: ({ rowState, row, updateRow }) => (
          <div className="table-row-title">
            <TransitionChevron
              isActive={rowState?.isExpanded}
              color="grey"
              thin
              wide
              onClick={(e) => {
                e.stopPropagation();
                updateRow(row.id)((rs) => ({
                  ...rs,
                  isExpanded: !(rs && rs.isExpanded),
                }));
              }}
              style={{ cursor: "pointer" }}
            />
            <LangLink
              to={`/advisory/${row.adv_id}`}
              style={{ textDecoration: "underline", color: "inherit" }}
              onClick={(e) => e.stopPropagation()}
            >
              {row.title}
            </LangLink>
          </div>
        ),
      },
      {
        header: t("secPortal_table_cvssScore.other"),
        accessor: "score",
        id: "kb_score",
        width: 70,
        Renderer: ({ row }) => (
          <div className="table-score">
            <BulletIcon
              style={{
                marginRight: "4px",
                verticalAlign: "middle",
                "--icon-primary": getSeverityColor(row.score),
              }}
            />
            <span>{row.score}</span>
          </div>
        ),
      },
      {
        header: t("secPortal_table_cveId.other"),
        accessor: "cve_id",
        id: "kb_cve",
        width: 90,
        Renderer: ({ row }) => (
          <div className="table-cve">
            <span className="table-score-string">
              {row.cve_id.length > 1 ? `${row.cve_id[0]}...` : `${row.cve_id}   `}
            </span>
            <CopyReplicateIcon
              style={{ cursor: "pointer" }}
              onClick={(event) => handleCopy(event, row.cve_id)}
            />
          </div>
        ),
      },
      { header: t("secPortal_table_published.other"), accessor: "published_date", id: "published_date", width: 40 },
      { header: t("secPortal_table_updated.other"), accessor: "updated_date", id: "updated_date", width: 40 },
    ];
  }, [t]);

  const {
    pagination,
    organizedRows,
    sortState,
    toggleSort,
    rowState,
    updateRowState,
    rows,
    filterState,
    updateFilterState,
    toggleRowSelection,
    selectAllRows,
    gotoPage,
    resetFilters,
    selectionState,
  } = useTable({
    columns: tableColumns,
    rows: tableRows,
    pageSize: pageSize,
    totalCount: totalCount,
    isSorting: true,
    externalSort: true,
    externalFilter: true,
    defaultSortState: { column: "updated_date", sortOrder: "desc" },
  });

  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const buildApiUrl = (path) => new URL(`${process.env.REACT_APP_API_BASE_URL}${path}`, window.location.origin);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/products/`);
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        const data = await response.json();
        const productOptions = data.products?.map((product) => ({
          label: product,
          value: product,
          tooltip: product.length > 40 ? product : null,
        }));
        setProducts(productOptions);
      } catch (error) {
        console.error("Error fetching products:", error);
        setError(error);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchRecords = () => {
      const offset = pagination.pageIndex * pageSize;
      const url = buildApiUrl("/advisory/");

      url.searchParams.append("limit", pageSize);

      if (typeof pagination.pageIndex === "number") {
        url.searchParams.append("skip", pageSize * pagination.pageIndex);
      }

      if (sortState) {
        url.searchParams.append("order", sortState.sortOrder);
        url.searchParams.append("sort_by", sortState.column);
      }

      if (!_.isEmpty(filterState.columns)) {
        for (const [filterName, filterValue] of _.toPairs(filterState.columns)) {
          const filterValuesKeys = _.keys(filterValue.values);
          filterValuesKeys[0] && url.searchParams.append("filter", filterValuesKeys[0]);
        }
      }

      if (form.formState.product?.length > 0) {
        const selectedProducts = form.formState.product.map((p) => p.value);
        url.searchParams.append("product", selectedProducts.join(","));
      }

      if (form.formState.severity?.length > 0) {
        const selectedSeverities = form.formState.severity.map((s) => s.value);
        url.searchParams.append("severity", selectedSeverities.join(","));
      }

      if (keyword) {
        url.searchParams.append("keyword", keyword);
      }

      if (publishedStartDate) url.searchParams.append("published_start_date", publishedStartDate);
      if (publishedEndDate) url.searchParams.append("published_end_date", publishedEndDate);
      if (updatedStartDate) url.searchParams.append("updated_start_date", updatedStartDate);
      if (updatedEndDate) url.searchParams.append("updated_end_date", updatedEndDate);

      setIsLoading(true);

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            setTableRows([]);
            throw new Error(response.status);
          }
          return response.json();
        })
        .then((data) => {
          const advisories = Array.isArray(data.advisories) ? data.advisories : [];
          const formattedData = advisories.map((item) => {
            const scoringCalc = Array.isArray(item.kb_scoring_calc) ? item.kb_scoring_calc : [];
            const highestScore = scoringCalc.length
              ? scoringCalc.reduce(
                  (max, calc) => (calc.score > max.score ? calc : max),
                  { score: -Infinity }
                )
              : { range: "", score: "" };
            const range = highestScore.range
              ? `${highestScore.range.charAt(0).toUpperCase()}${highestScore.range.slice(1).toLowerCase()}`
              : "";
            return {
              title: item.kb_title,
              score: range ? `${range} (${highestScore.score})` : "",
              cve_id: Array.isArray(item.kb_cve) ? item.kb_cve : [],
              published_date: formatDate(item.published_date),
              updated_date: formatDate(item.updated_date),
              kb_summary: item.kb_summary,
              kb_impact: item.kb_impact,
              id: item._id,
              adv_id: item.adv_id,
              ntap_advisory_id: item.ntap_advisory_id,
            };
          });
          setTableRows(formattedData);
          setTotalCount(Number(data.total_count) || 0);
        })
        .catch((error) => {
          console.error(error.message);
          setError(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    fetchRecords();
  }, [
    pagination.pageIndex,
    sortState,
    filterState,
    keyword,
    publishedStartDate,
    publishedEndDate,
    updatedStartDate,
    updatedEndDate,
    pageSize,
    form.formState.severity,
    form.formState.product,
  ]);

  const fetchRecordsForCSV = async () => {
    const url = buildApiUrl("/advisory/");

    if (sortState) {
      url.searchParams.append("order", sortState.sortOrder);
      url.searchParams.append("sort_by", sortState.column);
    }

    if (!_.isEmpty(filterState.columns)) {
      for (const [filterName, filterValue] of _.toPairs(filterState.columns)) {
        const filterValuesKeys = _.keys(filterValue.values);
        filterValuesKeys[0] && url.searchParams.append("filter", filterValuesKeys[0]);
      }
    }

    if (form.formState.product?.length > 0) {
      const selectedProducts = form.formState.product.map((p) => p.value);
      url.searchParams.append("product", selectedProducts.join(","));
    }

    if (form.formState.severity?.length > 0) {
      const selectedSeverities = form.formState.severity.map((s) => s.value);
      url.searchParams.append("severity", selectedSeverities.join(","));
    }

    if (keyword) {
      url.searchParams.append("keyword", keyword);
    }

    if (publishedStartDate) url.searchParams.append("published_start_date", publishedStartDate);
    if (publishedEndDate) url.searchParams.append("published_end_date", publishedEndDate);
    if (updatedStartDate) url.searchParams.append("updated_start_date", updatedStartDate);
    if (updatedEndDate) url.searchParams.append("updated_end_date", updatedEndDate);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(response.statusText);
      const data = await response.json();

      if (form.formState.product?.length > 0) {
        const selectedProducts = form.formState.product.map((p) => p.value);
        for (const item of data.advisories) {
          let availableAffectedProducts = [];
          let availableInvestigatingProducts = [];

          item.kb_affected_list?.forEach((product) => {
            if (selectedProducts.includes(product)) {
              availableAffectedProducts.push(product);
            }
          });

          item.kb_investigating_list?.forEach((product) => {
            if (selectedProducts.includes(product)) {
              availableInvestigatingProducts.push(product);
            }
          });

          item.kb_affected_list = availableAffectedProducts;
          item.kb_investigating_list = availableInvestigatingProducts;
        }
      }

      for (const item of data.advisories) {
        const affectedSet = new Set(item.kb_affected_list || []);
        const investigatingSet = new Set(item.kb_investigating_list || []);

        item.kb_unaffected_list = [
          ...new Set(
            (item.kb_internal_notes || [])
              .map((note) => note.product)
              .filter(
                (product) =>
                  product &&
                  !affectedSet.has(product) &&
                  !investigatingSet.has(product),
              ),
          ),
        ];
      }

      function generateProductRows(item, productList, status) {
        if (!productList?.length) return [];
        return productList.map((fix) => ({
          title: item.kb_title,
          score: item.kb_scoring_calc?.map((calc) => `${calc.cve_id} ${calc.range} ${calc.score}`).join(", "),
          cve_id: item.kb_cve,
          published_date: formatDate(item.published_date),
          updated_date: formatDate(item.updated_date),
          id: item.ntap_advisory_id,
          description: item.kb_summary,
          kb_status: item.kb_status,
          kb_workarounds: item.kb_workarounds,
          comment:
            item.kb_rev_history.length > 0
              ? item.kb_rev_history[item.kb_rev_history.length - 1].comment
              : "",
          version:
            item.kb_rev_history.length > 0
              ? item.kb_rev_history[item.kb_rev_history.length - 1].version
              : "",
          date:
            item.kb_rev_history.length > 0
              ? item.kb_rev_history[item.kb_rev_history.length - 1].date
              : "",
          year:
            item.kb_rev_history.length > 0
              ? item.kb_rev_history[item.kb_rev_history.length - 1].date.substring(0, 4)
              : "",
          product: fix,
          product_status: status,
          fix_links: item.kb_fixes?.find((product) => product.product === fix)?.fixes?.map((f) => f?.link).join("\n"),
          burt: item.kb_internal_notes.find((note) => note.product === fix)?.burt,
          jira: item.kb_internal_notes.find((note) => note.product === fix)?.jira,
        }));
      }

      const formattedData = data.advisories.flatMap((item) => [
        ...generateProductRows(item, item.kb_affected_list, "Affected"),
        ...generateProductRows(item, item.kb_investigating_list, "Investigating"),
        ...generateProductRows(item, item.kb_unaffected_list, "Not Affected"),
      ]);

      const csvData = convertToCSV(formattedData);
      downloadCSV(csvData, "advisories.csv");
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  const RenderTablePager = useMemo(
    () => (
      <TablePager
        pageRows={tableRows || []}
        pageSize={pageSize}
        rows={rowsCountArray}
        pageIndex={pagination.pageIndex}
        pageCount={pageCount}
        gotoPage={gotoPage}
      />
    ),
    [tableRows, pageSize, pagination.pageIndex, pageCount]
  );

  const SubRowRenderer = memo(({ row }) => {
    return (
      <div
        style={{
          backgroundColor: "var(--background-content)",
          width: "100%",
          padding: "14px",
        }}
      >
        <Layout.Grid centerContent className="sub-row">
          <Layout.GridItem lg={11}>
            <Heading level={4}>{t("secPortal_table_cveIds.other")}</Heading>
            <Text>{row.cve_id.join(", ")}</Text>
          </Layout.GridItem>

          <Layout.GridItem lg={11}>
            <Heading level={4}>{t("secPortal_table_impact.other")}</Heading>
            <Text dangerouslySetInnerHTML={{ __html: row?.kb_impact }} />
          </Layout.GridItem>

          <Layout.GridItem lg={11}>
            <Heading level={4}>{t("secPortal_table_summary.other")}</Heading>
            <Text>{row.kb_summary}</Text>
          </Layout.GridItem>

          <Layout.GridItem lg={11}>
            <Button
              onClick={() => {
                setSelectedCVE(row.cve_id);
                navigate(`/advisory/${row.adv_id}`, {
                  state: {
                    ntap_advisory_id: row.ntap_advisory_id,
                    cve: row.cve_id,
                    advisory: row.title,
                  },
                });
              }}
            >
              {t("advisory_viewFullAdvisory.other")}
            </Button>
            <Button
              color="secondary"
              style={{ marginLeft: "12px" }}
              onClick={() => openSubscribeModalFromTable(row.ntap_advisory_id)}
            >
              {t("advisory_subscribeEmailUpdates.other")}
            </Button>
          </Layout.GridItem>
        </Layout.Grid>
      </div>
    );
  });

  const onRowClick = useCallback(
    (row) => {
      updateRowState(row.id)((rs) => ({
        isExpanded: !(rs && rs.isExpanded),
      }));
    },
    [updateRowState]
  );

  const handleKeywordChange = (event) => {
    setKeyword(event.target.value);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <div>
      <div className="filters-frame">
        <div className="filter-container-left">
          <div className="filter-item">
            <section className="filter-text-sec">{t("secPortal_filter_searchByKeywords.other")}</section>
            <input
              className="input-box"
              type="text"
              placeholder={t("secPortal_table_keyword.other")}
              value={keyword}
              onChange={handleKeywordChange}
            />
          </div>

          <div className="filter-item-product">
            <section className="filter-text-sec">{t("secPortal_filter_byProduct.other")}</section>
            <FormFieldSelect
              name="product"
              form={form}
              options={products}
              isMulti={true}
              placeholder={t("secPortal_table_product.other")}
              isClearable={true}
              isSearchable={true}
              components={{ Option }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectStyles}
            />
          </div>

          <div className="filter-item">
            <section className="filter-text-sec">{t("secPortal_filter_bySeverity.other")}</section>
            <FormFieldSelect
              name="severity"
              form={form}
              options={[
                { label: t("severity_critical.other"), value: "CRITICAL" },
                { label: t("severity_high.other"), value: "HIGH" },
                { label: t("severity_medium.other"), value: "MEDIUM" },
                { label: t("severity_low.other"), value: "LOW" },
              ]}
              isMulti={true}
              placeholder={t("secPortal_table_severity.other")}
              isClearable={true}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectStyles}
            />
          </div>

          <div className="collapse-button-frame">
            <button className="collapse-button" onClick={toggleFilters}>
              {showFilters ? t("secPortal_filter_collapseOptions.other") : t("secPortal_filter_expandOptions.other")}
            </button>
          </div>
        </div>

        <div className="filter-container-right">
          <div className="export-button-frame">
            <Button color="secondary" onClick={fetchRecordsForCSV}>
              {t("secPortal_export.other")}
            </Button>
          </div>
        </div>
      </div>

      <div>
        {showFilters && (
          <div className="date-filter-container">
            <div className="date-filter-item">
              <div>
                <section className="filter-text-sec">{t("secPortal_filter_byPublishedDate.other")}</section>
              </div>
              <div className="date-inputs">
                <input
                  className="input-date-box"
                  type="date"
                  placeholder="Published Start Date"
                  value={publishedStartDate}
                  onChange={(e) => setPublishedStartDate(e.target.value)}
                />
                <label className="date-to-separator filter-text-sec">{t("secPortal_to.other")}</label>
                <input
                  className="input-date-box"
                  type="date"
                  placeholder="Published End Date"
                  value={publishedEndDate}
                  onChange={(e) => setPublishedEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className="date-filter-item">
              <div>
                <section className="filter-text-sec">{t("secPortal_filter_byUpdatedDate.other")}</section>
              </div>
              <div className="date-inputs">
                <input
                  className="input-date-box"
                  type="date"
                  placeholder="Updated Start Date"
                  value={updatedStartDate}
                  onChange={(e) => setUpdatedStartDate(e.target.value)}
                />
                <label className="date-to-separator filter-text-sec">{t("secPortal_to.other")}</label>
                <input
                  className="input-date-box"
                  type="date"
                  placeholder="Updated End Date"
                  value={updatedEndDate}
                  onChange={(e) => setUpdatedEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="table-frame">
        <div>
          <label htmlFor="page-size">{t("secPortal_pageSize.other")}</label>
          <select id="page-size" value={pageSize} onChange={handlePageSizeChange}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className="table-scroll">
          <Table
            isLoading={isLoading}
            headerContainerStyle={{ top: 0 }}
            columns={tableColumns}
            rows={tableRows}
            sortState={sortState || {}}
            toggleSort={toggleSort}
            rowState={rowState}
            toggleRowSelection={toggleRowSelection}
            filterState={filterState}
            updateFilterState={updateFilterState}
            filterOptions={{ products }}
            selectionState={selectionState}
            selectAllRows={selectAllRows}
            SubRowRenderer={SubRowRenderer}
            updateRowState={updateRowState}
            onRowClick={onRowClick}
            style={{ minWidth: 800 }}
          />
        </div>
      </div>

      {tableRows.length > 0 && totalCount >= tableRows.length && (
        <div>
          <TablePager
            pageRows={tableRows || []}
            pageSize={pageSize}
            rows={rowsCountArray}
            pageIndex={pagination.pageIndex}
            pageCount={pageCount}
            gotoPage={gotoPage}
          />
        </div>
      )}
    </div>
  );
};

export default Table1;